<template>
    <div v-if="show" id="id-goro-user-document-modal" class="cls-custom-popup-container cls-user-document-modal">
        <div class="popup-dialog popup-dialog-centered">
            <div class="popup-content">
                <div class="popup-body text-center">
                    <div class="body-content text-left">
                        <h2 class="heading font-24 font-bold text-center mt-2 mb-4">
                            {{ $t('DOCUMENT_VERSION.TITLE') }}
                        </h2>

                        <p v-if="!isAllAutoAccepted" class="subtitle">{{ $t('DOCUMENT_VERSION.I_HAVE_READ_AND_AGREED')
                            }}:
                        </p>

                        <!-- Non-separated: all docs share one box -->
                        <div v-if="!isSeparated" class="check-wrapper mt-2">
                            <div v-for="(document, index) in sortedDocuments" :key="document.uuid"
                                class="check-container d-flex" :class="{ 'mt-2': index > 0 }">
                                <template v-if="!isAutoAccepted(document)">
                                    <img @click="toggleChecked(document.uuid)" v-if="checkedData[document.uuid]"
                                        src="@/assets/img/checkbox-agreement-checked.svg" alt="checked">
                                    <img @click="toggleChecked(document.uuid)" v-else
                                        src="@/assets/img/checkbox-agreement-unchecked.svg" alt="unchecked">
                                </template>
                                <div>
                                    <p>
                                        <span @click="openDocument(document)">{{ getType(document) }}</span>
                                    </p>
                                    <template v-if="isAutoAccepted(document)">
                                        <p class="effective-from mt-1">{{ autoAcceptedEffectiveFrom(document) }}</p>
                                        <p v-if="!isMultiple" class="note mt-1">{{ autoAcceptedNote(document) }}</p>
                                    </template>
                                </div>
                            </div>
                            <p v-if="isAllAutoAccepted && isMultiple" class="note mt-3 ml-2">
                                {{ $t('DOCUMENT_VERSION.BOTH_UPDATED') }}
                            </p>
                        </div>

                        <!-- Separated: each doc in its own box -->
                        <template v-else>
                            <div v-for="document in sortedDocuments" :key="document.uuid" class="check-wrapper mt-2">
                                <div class="check-container d-flex">
                                    <template v-if="!isAutoAccepted(document)">
                                        <img @click="toggleChecked(document.uuid)" v-if="checkedData[document.uuid]"
                                            src="@/assets/img/checkbox-agreement-checked.svg" alt="checked">
                                        <img @click="toggleChecked(document.uuid)" v-else
                                            src="@/assets/img/checkbox-agreement-unchecked.svg" alt="unchecked">
                                    </template>
                                    <div>
                                        <p>
                                            <span @click="openDocument(document)">{{ getType(document) }}</span>
                                        </p>
                                        <template v-if="isAutoAccepted(document)">
                                            <p class="effective-from mt-1">{{ autoAcceptedEffectiveFrom(document) }}</p>
                                            <p class="note mt-1">{{ autoAcceptedNote(document) }}</p>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <div v-if="!isAllAutoAccepted" class="acknowledgement-box">
                            {{ $t('DOCUMENT_VERSION.ACKNOWLEDGEMENT') }}
                        </div>

                        <p v-if="showError" class="error-text text-center mt-3">
                            {{ $t('DOCUMENT_VERSION.PLEASE_CHECK_AGREEMENT') }}
                        </p>

                        <b-button @click="onAgree" class="btn-agree btn btn-none btn-main font-18 font-medium mt-3">
                            {{ isAllAutoAccepted ? $t("AUTH.CONTINUE") : $t("common.AGREE") }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DOCUMENT_VERSIONING_TYPES } from "../constants/constants"
import documentVersioningService from "../services/document_versioning.service";

export default {

    props: {
        show: {
            type: Boolean,
            default: false,
        },
        documents: {
            type: Array,
            default: null,
        },
    },

    data() {
        return {
            checkedData: {},
            showError: false,
        }
    },

    watch: {
        show: {
            immediate: true,
            handler(value) {
                if (typeof document === 'undefined') return
                if (value) {
                    document.body.style.overflow = 'hidden'
                } else {
                    document.body.style.overflow = ''
                }
            },
        },
    },

    beforeUnmount() {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = ''
        }
    },

    methods: {
        getType(document) {
            if (document.doc_type === DOCUMENT_VERSIONING_TYPES.TERMS_AND_CONDITION) {
                return this.$t('FOOTER.TERMS_AND_CONDITIONS')
            }
            return this.$t('FOOTER.PRIVACY_POLICY')
        },

        openDocument(document) {
            if (document.doc_type === DOCUMENT_VERSIONING_TYPES.TERMS_AND_CONDITION) {
                const routeData = this.$router.resolve({
                    name: "termsAndConditions",
                    query: { uuid: document.uuid }
                });
                window.open(routeData.href, '_blank');
            } else {
                const routeData = this.$router.resolve({
                    name: "privacyPolicy",
                    query: { uuid: document.uuid }
                });
                window.open(routeData.href, '_blank');
            }
        },

        toggleChecked(uuid) {
            const isChecked = this.checkedData[uuid] || false
            this.checkedData[uuid] = !isChecked
            if (this.allChecked) {
                this.showError = false
            }
        },

        async onAgree() {
            if (!this.allChecked) {
                this.showError = true
                return
            }
            this.showError = false
            try {
                for (const document of this.documents) {
                    await documentVersioningService.markAgreeDocument({
                        doc_uuid: document.uuid,
                        user_acknowledge: document.acceptance_source === 'AUTO',
                    })
                }
                await this.$store.dispatch('getDocumentVersioning')
            } catch (err) { }
        },

        isAutoAccepted(doc) {
            return doc.acceptance_source === 'AUTO'
        },

        autoAcceptedEffectiveFrom(doc) {
            const date = new Date(doc.auto_accept_date).toLocaleDateString(this.$i18n.locale, {
                year: 'numeric', month: 'long', day: 'numeric'
            })
            return this.$t('DOCUMENT_VERSION.EFFECTIVE_FROM', { date })
        },

        autoAcceptedNote(doc) {
            if (doc.doc_type === DOCUMENT_VERSIONING_TYPES.TERMS_AND_CONDITION) {
                return this.$t('DOCUMENT_VERSION.TERMS_OF_SERVICE_UPDATED')
            }
            return this.$t('DOCUMENT_VERSION.PRIVACY_POLICY_UPDATED')
        },
    },

    computed: {
        allChecked() {
            if (!this.documents) {
                return false
            }
            return Object.values(this.checkedData).filter(v => v).length === this.documents.filter(d => d.acceptance_source !== 'AUTO').length
        },

        isMultiple() {
            return !!this.documents && this.documents.length > 1
        },

        isSeparated() {
            if (!this.isMultiple) return false
            const hasNull = this.documents.some(d => !d.acceptance_source)
            return hasNull && this.documents[0].acceptance_source !== this.documents[1].acceptance_source
        },

        isAllAutoAccepted() {
            if (!this.documents || this.documents.length === 0) return false
            return this.documents.every(doc => doc.acceptance_source === 'AUTO')
        },

        sortedDocuments() {
            if (!this.documents) return []
            return [...this.documents].sort((a, b) => {
                const aAuto = a.acceptance_source === 'AUTO' ? 0 : 1
                const bAuto = b.acceptance_source === 'AUTO' ? 0 : 1
                return aAuto - bAuto
            })
        },
    }
}
</script>

<style lang="scss" scoped>
.cls-user-document-modal {
    .popup-content {
        border-radius: 25px;
        max-width: 500px;

        @media(max-width: 768px) {
            max-width: calc(100% - 1rem);
        }

        .popup-body {
            position: relative;
            z-index: 1;
            background-color: white;

            .body-content {
                padding: 30px;

                .subtitle {
                    color: #8A8A8A;
                    font-size: 15px;
                    margin-bottom: 12px;
                    font-weight: 500;
                }

                .check-wrapper {
                    background-color: #F2F4F4;
                    padding: 16px;
                    border-radius: 12px;
                }

                .check-container {
                    padding: 6px 0;

                    img {
                        width: 20px;
                        height: 20px;
                        margin-top: 2px;
                        cursor: pointer;
                    }

                    p {
                        font-size: 15px;
                        margin-left: 12px;
                        margin-bottom: 0;
                        font-weight: 500;

                        span {
                            text-decoration: underline;
                            color: var(--primary-darker-color);
                            font-weight: 600;
                            cursor: pointer;

                            &:hover {
                                font-weight: 700;
                                color: var(--primary-color);
                            }
                        }
                    }

                    .effective-from {
                        font-weight: 600;
                        font-size: 14px;
                        margin-bottom: 0;
                    }

                    .note {
                        font-weight: 500;
                        font-size: 14px;
                        margin-left: 12px;
                    }
                }

                .acknowledgement-box {
                    background-color: #FF8C00;
                    color: #fff;
                    font-size: 14px;
                    font-weight: 600;
                    text-align: center;
                    padding: 16px;
                    border-radius: 12px;
                    margin-top: 20px;
                    line-height: 1.5;
                }

                .error-text {
                    color: #BA1A1A;
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 0;
                }

                .btn-agree {
                    width: 100%;
                    padding: 16px;
                }
            }

        }
    }
}
</style>
